<template>
  <div>
    <div v-if="component" class="comp-name mb-2">
      <b>{{ component.name }}</b>
    </div>
    <div class="d-flex flex-column section-spacing-20">
      <TextField
        v-model="formData.manufacturer"
        :disabled="
          readOnly || (useSameManufacturer && !showUseSameManufacturer)
        "
        label="Manufacturer"
        name="manufacturer"
        class="full-width"
      >
        <template #right-label>
          <div
            v-if="showUseSameManufacturer"
            class="useSameManufacturerCheckbox text-sm"
          >
            <Checkbox
              v-model="internalUseSameManufacturer"
              label="Use Same Manufacturer for All Components"
            />
          </div>
        </template>
      </TextField>
      <TextField
        v-model="formData.install_location"
        label="Location"
        name="install_location"
        hint-label="(Garage, attic, etc.)"
        class="full-width"
        :disabled="readOnly"
      />
      <TextField
        v-model="formData.warranty"
        label="Warranty"
        name="warranty"
        class="full-width"
        :disabled="readOnly"
      />
      <div class="d-flex flex-evenly mb-2">
        <TextField
          v-model="formData.model_number"
          label="Model Number"
          name="model_number"
          labelClass="flex-1 mr-3"
          :disabled="readOnly"
        />
        <TextField
          v-model="formData.serial_number"
          label="Serial Number"
          name="serial_number"
          class="flex-1"
          :disabled="readOnly"
        />
      </div>
      <div class="d-flex flex-evenly">
        <div class="flex-1 mr-3">
          <Datepicker
            v-model="formData.manufactured_on"
            data-cy="manufactured_on"
            name="manufactured_on"
            label="Manufacture Date"
            hint-label="(MM/YY)"
            placeholder="MM/YY"
            type="month"
            format="MM/YY"
            :disabled-date="isFutureDate"
            :validation-errors="validationErrors"
            :disabled="readOnly"
          />
        </div>
        <div class="flex-1">
          <Datepicker
            v-model="formData.installed_on"
            data-cy="installed_on"
            name="installed_on"
            label="Installation Date"
            hint-label="(MM/DD/YY)"
            placeholder="MM/DD/YY"
            :disabled-date="isFutureDate"
            :validation-errors="validationErrors"
            :disabled="readOnly"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { isFutureDate } from "@/lib/dates.js";
import { mustNotBeFutureDate } from "@/lib/vuelidate_helpers";

import Checkbox from "@/components/Checkbox.vue";
import Datepicker from "@/components/Datepicker.vue";
import TextField from "@/components/TextField.vue";
import useVuelidate from "@vuelidate/core";

export default {
  components: {
    Checkbox,
    TextField,
    Datepicker
  },
  props: {
    useSameManufacturer: {
      type: Boolean,
      default: true
    },
    showUseSameManufacturer: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    component: {
      type: Object,
      default: null
    },
    validationErrors: {
      type: Object,
      default: null
    }
  },
  emits: ["update:form-data", "update:use-same-manufacturer"],
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    return {
      formData: {
        archived: false,
        manufacturer: "",
        model_number: "",
        serial_number: "",
        warranty: "",
        install_location: "",
        installed_on: "",
        manufactured_on: "",
        ...this.component
      }
    };
  },
  computed: {
    internalUseSameManufacturer: {
      get() {
        return this.useSameManufacturer;
      },
      set(val) {
        this.$emit("update:use-same-manufacturer", val);
      }
    }
  },
  validations() {
    return {
      formData: {
        manufactured_on: {
          $autoDirty: true,
          maxValue: mustNotBeFutureDate(
            `Cannot use future time. Current time ${moment().format("h:mm a")}.`
          )
        },
        installed_on: {
          $autoDirty: true,
          maxValue: mustNotBeFutureDate(
            `Cannot use future time. Current time ${moment().format("h:mm a")}.`
          )
        }
      }
    };
  },
  watch: {
    formData: {
      handler(val) {
        this.$emit("update:form-data", val);
      },
      deep: true
    },
    component(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.formData = {
          archived: false,
          manufacturer: "",
          model_number: "",
          serial_number: "",
          warranty: "",
          install_location: "",
          installed_on: "",
          manufactured_on: "",
          ...this.component
        };
      }
    }
  },
  methods: {
    isFutureDate
  }
};
</script>

<style lang="scss" scoped>
.comp-name {
  font-size: 1.3em;
}
</style>
